import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { InteractiveMap } from '../components/markets/InteractiveMap';
import { markets } from '../data/mock';
import { Button } from '../components/ui/button';
import { MapPin, ArrowRight, TrendingUp, FileText } from 'lucide-react';
import { SEO } from '../components/SEO';

export default function MarketsPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Global Markets"
        path="/markets"
        description="Discover the global markets Naviton Global serves across Asia-Pacific, Middle East, Europe, and the Americas with Australian domestic network coverage."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-navy-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-500/20 text-teal-400 text-sm font-medium mb-6">
                Global Presence
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Markets We <span className="text-teal-400">Trade With</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Strategic presence across key international markets, with deep expertise 
                in trade regulations and local business practices for imports and exports.
              </p>
            </div>
          </div>
        </section>

        {/* Australian Domestic Network Section */}
        <section className="py-16 bg-gradient-to-br from-teal-50 to-emerald-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 rounded-full bg-teal-500/20 text-teal-700 text-sm font-medium mb-4">
                  Nationwide Coverage
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-6">
                  Australian Domestic Network
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Beyond international trade, we provide comprehensive domestic logistics and distribution 
                  services across Australia. Our nationwide network connects businesses from coast to coast, 
                  ensuring reliable interstate trade support.
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {[
                    { city: 'Sydney', region: 'NSW' },
                    { city: 'Melbourne', region: 'VIC' },
                    { city: 'Brisbane', region: 'QLD' },
                    { city: 'Perth', region: 'WA' },
                    { city: 'Adelaide', region: 'SA' },
                    { city: 'Darwin', region: 'NT' },
                  ].map((location) => (
                    <div key={location.city} className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                      <div className="p-2 rounded-lg bg-teal-100">
                        <MapPin className="h-4 w-4 text-teal-600" />
                      </div>
                      <div>
                        <span className="font-semibold text-navy-900">{location.city}</span>
                        <span className="text-slate-500 text-sm ml-2">{location.region}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/services#domestic-trade">
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                    Explore Domestic Services
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=800&q=80" 
                  alt="Australian landscape representing nationwide coverage"
                  className="rounded-3xl shadow-xl w-full h-auto"
                />
                <div className="absolute -bottom-6 -left-6 bg-navy-900 text-white p-6 rounded-2xl shadow-lg">
                  <div className="text-3xl font-bold text-teal-400">100%</div>
                  <div className="text-sm text-slate-300">Australian Coverage</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Interactive World Map */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <InteractiveMap />
          </div>
        </section>

        {/* Markets Detail */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-navy-900 mb-4">Our Key Markets</h2>
              <p className="text-lg text-slate-600">Detailed insights into the regions we serve</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {markets.map((market) => (
                <div
                  key={market.id}
                  className="bg-white p-8 rounded-2xl border border-slate-200 hover:border-teal-300 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-navy-900 mb-1">
                        {market.name}
                      </h3>
                      <span className="text-sm text-teal-600 font-medium">{market.region}</span>
                    </div>
                    <div className="p-2 rounded-lg bg-teal-100">
                      <MapPin className="h-5 w-5 text-teal-600" />
                    </div>
                  </div>

                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {market.description}
                  </p>

                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-navy-900 mb-3">Key Highlights:</h4>
                    <ul className="space-y-2">
                      {market.highlights.map((highlight) => (
                        <li key={highlight} className="flex items-start gap-2 text-sm text-slate-600">
                          <TrendingUp className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-navy-900 mb-3">Trade Focus:</h4>
                    <div className="flex flex-wrap gap-2">
                      {market.tradeFocus.map((focus) => (
                        <span
                          key={focus}
                          className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
                        >
                          {focus}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trade Compliance Note */}
        <section className="py-16 bg-navy-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <FileText className="h-12 w-12 text-teal-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Trade Compliance Across All Markets
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              We ensure full compliance with bilateral trade agreements, customs regulations, 
              and import/export requirements for each market we operate in.
            </p>
            <Link to="/compliance">
              <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                View Our Compliance Standards
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
